<template>
  <div v-if="maxMode" class="validation-mini-card selected-platform-card validation-mini-card--max-mode">
    <div class="validation-mini-card__max-header">
      <v-tooltip location="top" :open-delay="120">
        <template #activator="{ props: nameTipProps }">
          <div class="selected-platform-card__title validation-mini-card__max-title" v-bind="nameTipProps">
            {{ sessionName }}
          </div>
        </template>
        <span>{{ sessionName }}</span>
      </v-tooltip>

      <div class="validation-mini-card__max-vendor-logo-wrap">
        <img
          v-if="vendorLogoSrc"
          :src="vendorLogoSrc"
          :alt="vendorName"
          class="validation-mini-card__max-vendor-logo"
        >
        <div v-else class="validation-mini-card__max-vendor-fallback">
          {{ vendorBadgeText }}
        </div>
      </div>
    </div>

    <hr class="selected-platform-card__description-divider validation-mini-card__max-divider">

    <div class="selected-platform-card__content-row validation-mini-card__max-content-row">
      <div class="validation-mini-card__max-left-col">
        <div class="selected-platform-card__image-wrap validation-mini-card__max-image-wrap">
          <img
            v-if="platformImageSrc"
            :src="platformImageSrc"
            :alt="platformName"
            class="selected-platform-card__image validation-mini-card__max-image"
          >
          <div v-else class="selected-platform-card__image-fallback validation-mini-card__max-image-fallback">
            No platform image
          </div>
        </div>

        <div class="validation-mini-card__max-uid-row validation-mini-card__max-uid-row--body">
          <button type="button" class="validation-mini-card__uid-button" @click="openValidationDetails">
            <EncodedIdField
              :id="sessionId"
              :type="'validation'"
              :hideLabel="true"
            />
          </button>
        </div>
      </div>

      <div class="selected-platform-card__details validation-mini-card__max-details">
        <div class="selected-platform-card__specs validation-mini-card__max-copy">
          <div v-if="platformSpecRows.length > 0" class="validation-mini-card__max-spec-grid">
            <div
              v-for="(row, index) in platformSpecRows"
              :key="`${row.key}-${index}`"
              class="validation-mini-card__max-spec-row"
            >
              <div class="validation-mini-card__max-spec-key">{{ row.key }}</div>
              <div class="validation-mini-card__max-spec-value">{{ row.value }}</div>
            </div>
          </div>
          <div v-else class="validation-mini-card__max-platform-description validation-mini-card__max-platform-description--muted">
            No platform specs available.
          </div>
        </div>
      </div>
    </div>
  </div>

  <div v-else class="validation-mini-card">
    <div class="validation-mini-card__left">
      <v-tooltip location="top" :open-delay="120">
        <template #activator="{ props: nameTipProps }">
          <div class="validation-mini-card__name" v-bind="nameTipProps">
            {{ sessionName }}
          </div>
        </template>
        <span>{{ sessionName }}</span>
      </v-tooltip>
      <div class="validation-mini-card__uid">
        <button type="button" class="validation-mini-card__uid-button" @click="openValidationDetails">
          <EncodedIdField
            :id="sessionId"
            :type="'validation'"
            :hideLabel="true"
          />
        </button>
      </div>
    </div>

    <div class="validation-mini-card__platform-block">
      <div class="validation-mini-card__col2">
        <v-tooltip v-if="platformImageSrc" location="right" :open-delay="80" content-class="validation-mini-card-image-tooltip">
          <template #activator="{ props: imageTipProps }">
            <div class="validation-mini-card__image-wrap" v-bind="imageTipProps">
              <img
                :src="platformImageSrc"
                :alt="platformName"
                class="validation-mini-card__platform-image"
              >
            </div>
          </template>

          <img
            :src="platformImageSrc"
            :alt="platformName"
            class="validation-mini-card__image-preview-img"
          >
        </v-tooltip>

        <div v-else class="validation-mini-card__platform-placeholder">No Image</div>
      </div>

      <v-tooltip location="top" :open-delay="120" content-class="validation-mini-card-tooltip" :disabled="platformTooltipEntries.length === 0">
        <template #activator="{ props: tipProps }">
          <div class="validation-mini-card__col3" v-bind="tipProps">
            <div class="validation-mini-card__platform-name">
              {{ platformName }}
            </div>
            <div class="validation-mini-card__platform-host">
              {{ platformHost }}
            </div>
          </div>
        </template>

        <div v-if="platformTooltipEntries.length > 0" class="validation-mini-card__tooltip-content">
          <div
            v-for="entry in platformTooltipEntries"
            :key="entry.key"
            class="validation-mini-card__tooltip-row"
          >
            <span class="validation-mini-card__tooltip-key">{{ entry.key }}:</span>
            <span class="validation-mini-card__tooltip-value">{{ entry.value }}</span>
          </div>
        </div>
      </v-tooltip>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import EncodedIdField from '@/components/EncodedIdField.vue';
import { encodeID } from '@/assets/js/utils.js';

const props = defineProps({
  session: {
    type: Object,
    default: () => ({}),
  },
  maxMode: {
    type: Boolean,
    default: false,
  },
});

const router = useRouter();
const route = useRoute();
const maxMode = computed(() => props.maxMode);

const sessionId = computed(() => Number(
  props.session?.id
  || props.session?.validate_session_id
  || props.session?.val_session_id
  || 0,
));

const sessionName = computed(() => (
  props.session?.docker_task?.name
  || props.session?.name
  || props.session?.title
  || `Validation ${sessionId.value || '-'}`
));

function parseMaybeJson(value) {
  if (typeof value !== 'string') {
    return value;
  }

  const trimmed = value.trim();
  if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) {
    return value;
  }

  try {
    return JSON.parse(trimmed);
  } catch (error) {
    return value;
  }
}

const platformInstance = computed(() => {
  const source = parseMaybeJson(
    props.session?.platform_instance
    || props.session?.params?.platform_instance
    || props.session?.platform
    || {},
  );

  if (!source || typeof source !== 'object' || Array.isArray(source)) {
    return {};
  }

  return source;
});

const vendorSource = computed(() => {
  const source =
    parseMaybeJson(platformInstance.value?.vendor)
    || parseMaybeJson(platformInstance.value?.platform?.vendor)
    || parseMaybeJson(props.session?.vendor)
    || parseMaybeJson(props.session?.params?.vendor)
    || {};

  if (!source || typeof source !== 'object' || Array.isArray(source)) {
    return {};
  }

  return source;
});

const platformDescription = computed(() => (
  platformInstance.value?.description
  || platformInstance.value?.platform_description
  || props.session?.platform_description
  || props.session?.params?.platform_description
  || ''
));

const vendorName = computed(() => (
  vendorSource.value?.name
  || platformInstance.value?.vendor?.name
  || platformInstance.value?.vendor_name
  || vendorSource.value?.code
  || platformInstance.value?.vendor?.code
  || platformInstance.value?.vendor_code
  || props.session?.vendor_code
  || ''
));

const vendorBadgeText = computed(() => {
  const name = String(vendorName.value || '').trim();
  if (!name) {
    return 'V';
  }
  return name.slice(0, 2).toUpperCase();
});

const platformName = computed(() => (
  platformInstance.value?.name
  || platformInstance.value?.platform_name
  || platformInstance.value?.platform
  || props.session?.platform_name
  || props.session?.params?.platform_name
  || 'Platform (N/A)'
));

const platformHost = computed(() => (
  platformInstance.value?.host
  || platformInstance.value?.host_name
  || platformInstance.value?.hostname
  || platformInstance.value?.ip
  || platformInstance.value?.address
  || 'Host (N/A)'
));

const platformImageSrc = computed(() => {
  const directSource = resolveImageSource(platformInstance.value, ['image', 'image_url', 'imageUrl', 'logo', 'logo_url', 'logoUrl'])
    || resolveImageSource(props.session, ['platform_image', 'platform_logo'])
    || resolveImageSource(props.session?.params, ['platform_image', 'platform_logo']);

  if (directSource) {
    return directSource;
  }

  const platformCode = String(
    platformInstance.value?.code
    || platformInstance.value?.platform_code
    || props.session?.platform_code
    || '',
  ).trim();

  return platformCode ? `/platform/${encodeURIComponent(platformCode)}/image` : '';
});

const vendorLogoSrc = computed(() => {
  const directSource = resolveImageSource(vendorSource.value, ['logo', 'logo_url', 'logoUrl', 'image', 'image_url', 'imageUrl'])
    || resolveImageSource(platformInstance.value, ['vendor_logo', 'vendor_logo_url'])
    || resolveImageSource(props.session, ['vendor_logo', 'vendor_logo_url'])
    || resolveImageSource(props.session?.params, ['vendor_logo', 'vendor_logo_url']);

  if (directSource) {
    return directSource;
  }

  const vendorCode = String(
    vendorSource.value?.code
    || platformInstance.value?.vendor?.code
    || platformInstance.value?.vendor_code
    || props.session?.vendor_code
    || props.session?.params?.vendor_code
    || '',
  ).trim();

  return vendorCode ? `/vendor/${encodeURIComponent(vendorCode)}/logo` : '';
});

function resolveImageSource(source, keys) {
  if (!source || typeof source !== 'object') {
    return '';
  }

  for (const key of keys) {
    const value = source[key];
    const normalized = normalizeImageValue(value);
    if (normalized) {
      return normalized;
    }
  }

  return '';
}

function normalizeImageValue(value) {
  if (!value) {
    return '';
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) {
      return '';
    }
    if (trimmed.startsWith('data:image') || trimmed.startsWith('http://') || trimmed.startsWith('https://') || trimmed.startsWith('/')) {
      return trimmed;
    }
    return `data:image/${detectMimeTypeFromBase64(trimmed)};base64,${trimmed}`;
  }

  if (Array.isArray(value) && value.length > 0) {
    const binary = new Uint8Array(value);
    return bytesToDataUrl(binary);
  }

  if (value instanceof Uint8Array) {
    return bytesToDataUrl(value);
  }

  return '';
}

function bytesToDataUrl(bytes) {
  let binary = '';
  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });
  const mimeType = detectMimeTypeFromText(binary);
  return `data:image/${mimeType};base64,${btoa(binary)}`;
}

function detectMimeTypeFromBase64(base64Value) {
  try {
    return detectMimeTypeFromText(atob(base64Value));
  } catch (error) {
    return 'png';
  }
}

function detectMimeTypeFromText(text) {
  const normalized = String(text || '').trimStart();
  if (normalized.startsWith('<svg') || normalized.startsWith('<?xml') || normalized.includes('<svg')) {
    return 'svg+xml';
  }

  return 'png';
}

const platformTooltipEntries = computed(() => {
  const source = platformInstance.value;
  if (!source || typeof source !== 'object') {
    return [];
  }

  return Object.entries(source)
    .filter(([key, value]) => key && value !== undefined && value !== null && String(value).trim() !== '')
    .map(([key, value]) => ({
      key,
      value: typeof value === 'object' ? JSON.stringify(value) : String(value),
    }));
});

  const isPublicRoute = computed(() => route.path.includes('/public'));

const platformSpecRows = computed(() => {
  const rows = [
    {
      key: 'Platform',
      value: platformName.value,
    },
  ];

  if (platformDescription.value) {
    rows.push({
      key: 'Description',
      value: platformDescription.value,
    });
  }

  if (platformHost.value && platformHost.value !== 'Host (N/A)') {
    rows.push({
      key: 'Host',
      value: platformHost.value,
    });
  }

  return rows.concat(normalizeSpecs(platformInstance.value?.specs));
});

function normalizeSpecs(specs) {
  if (specs && typeof specs === 'object' && !Array.isArray(specs)) {
    return Object.entries(specs)
      .filter(([key, value]) => key && value !== undefined && value !== null && String(value).trim() !== '')
      .map(([key, value]) => ({
        key: formatSpecKey(key),
        value: String(value),
      }));
  }

  if (Array.isArray(specs)) {
    return specs
      .map((item, index) => ({
        key: `Item ${index + 1}`,
        value: String(item),
      }))
      .filter((row) => row.value.trim() !== '');
  }

  if (typeof specs === 'string') {
    const trimmed = specs.trim();
    if (!trimmed) {
      return [];
    }

    try {
      const parsed = JSON.parse(trimmed);
      if (parsed && typeof parsed === 'object') {
        return normalizeSpecs(parsed);
      }
    } catch (error) {
      // Continue with text parsing when specs is not valid JSON.
    }

    return trimmed
      .split('|')
      .map((chunk) => chunk.trim())
      .filter(Boolean)
      .map((chunk) => {
        const separatorIndex = chunk.indexOf(':');
        if (separatorIndex === -1) {
          return { key: 'Spec', value: chunk };
        }

        const key = chunk.slice(0, separatorIndex).trim();
        const value = chunk.slice(separatorIndex + 1).trim();
        return {
          key: formatSpecKey(key || 'Spec'),
          value,
        };
      })
      .filter((row) => row.value);
  }

  return [];
}

function formatSpecKey(key) {
  return String(key)
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function openValidationDetails() {
  if (!sessionId.value) {
    return;
  }

  router.push({
    name: `${isPublicRoute.value ? 'public.' : ''}experiment.validation.details`,
    params: {
      sessionId: encodeID(sessionId.value, 'v'),
    },
  });
}

</script>

<style lang="scss" scoped>
.validation-mini-card {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  align-items: center;
  gap: 8px;
  border: 1px solid #d0d7e2;
  border-radius: 8px;
  padding: 5px 8px;
  background: #ffffff;
}

.validation-mini-card--max-mode {
  display: flex;
  flex-direction: column;
  gap: 10px;
  flex-grow: 1;
  align-items: center;
  margin: 4px;
  position: relative;
  min-width: 400px;
  max-width: 600px;
  min-height: 0;
  padding: 10px;
  border-radius: 4px;
  text-align: left;
  cursor: default;
  background: #ffffff;
  color: #2f2f2f;
  border: 1px solid #d5dbe6;

  &:hover {
    box-shadow: 0 0 0 1px #2563eb;
  }
}

.validation-mini-card__max-header {
  width: 100%;
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: start;
  column-gap: 8px;
}

.validation-mini-card__max-title {
  width: 100%;
  font-size: 0.95rem;
  font-weight: 700;
  line-height: 1.3;
  text-align: left;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  white-space: normal;
  word-break: break-word;
}

.validation-mini-card__max-vendor-logo-wrap {
  width: 56px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.validation-mini-card__max-vendor-logo {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.validation-mini-card__max-vendor-fallback {
  width: 100%;
  height: 100%;
  border: 1px solid #d5dbe6;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.68rem;
  font-weight: 700;
  color: #475569;
  background: #f8fafc;
}

.validation-mini-card__max-uid-row {
  width: 100%;
  margin-top: -6px;
  font-size: 0.78rem;
}

.validation-mini-card__max-left-col {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
}

.validation-mini-card__max-uid-row--body {
  margin-top: auto;
  padding-top: 6px;
  text-align: left;
}

.validation-mini-card__max-divider {
  width: 100%;
  margin: -4px 0 0;
  border: 0;
  border-top: 1px solid #d5dbe6;
}

.validation-mini-card__max-content-row {
  display: grid;
  grid-template-columns: 120px minmax(0, 1fr);
  gap: 0;
  margin-top: -2px;
  flex: 1;
  min-height: 0;
  align-items: start;
}

.validation-mini-card__max-image-wrap {
  width: auto;
  min-height: 0;
  padding: 0;
  border-radius: 0;
}

.validation-mini-card__max-image {
  display: block;
  margin: 0;
  width: auto;
  max-width: 120px;
  max-height: 120px;
  object-fit: contain;
}

.validation-mini-card__max-image-fallback {
  width: 100%;
  font-size: 0.78rem;
  text-align: left;
  color: #555555;
}

.validation-mini-card__max-details {
  min-width: 0;
  margin-left: 0;
}

.validation-mini-card__max-copy {
  color: #555555;
}

.validation-mini-card__max-spec-grid {
  display: flex;
  flex-direction: column;
}

.validation-mini-card__max-spec-row {
  display: grid;
  grid-template-columns: minmax(90px, 120px) minmax(0, 1fr);
  gap: 6px;
  align-items: start;
}

.validation-mini-card__max-spec-key {
  font-size: 0.78rem;
  font-weight: 600;
  color: #334155;
}

.validation-mini-card__max-spec-value {
  font-size: 0.78rem;
  line-height: 1.35;
  color: #475569;
  white-space: pre-wrap;
  word-break: break-word;
}

.validation-mini-card__max-platform-name {
  font-size: 0.96rem;
  font-weight: 700;
  color: #334155;
  margin-bottom: 2px;
}

.validation-mini-card__max-platform-description {
  font-size: 0.84rem;
  line-height: 1.45;
  color: #475569;
  white-space: pre-wrap;
  word-break: break-word;
}

.validation-mini-card__max-platform-description--muted {
  color: #94a3b8;
}

.validation-mini-card__left {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.validation-mini-card__name {
  font-size: 0.84rem;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.validation-mini-card__uid {
  font-size: 0.72rem;
}

.validation-mini-card__uid-button {
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  text-align: left;
  color: inherit;
}

.validation-mini-card__uid-button:hover {
  text-decoration: underline;
}

.validation-mini-card__platform-block {
  display: grid;
  grid-template-columns: 56px minmax(0, 1fr);
  align-items: center;
  gap: 8px;
  cursor: default;
}

.validation-mini-card__col2 {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  margin: 0;
}

.validation-mini-card__image-wrap {
  display: flex;
  align-items: center;
  justify-content: center;
}

.validation-mini-card__col3 {
  display: flex;
  flex-direction: column;
  min-width: 0;
  gap: 2px;
}

.validation-mini-card__platform-image {
  width: 48px;
  height: 48px;
  object-fit: contain;
  border-radius: 0;
  border: none;
  background: transparent;
}

.validation-mini-card__platform-placeholder {
  width: 48px;
  height: 48px;
  border-radius: 6px;
  border: 1px dashed #cbd5e1;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.5rem;
  color: #64748b;
}

.validation-mini-card__image-preview-img {
  width: 260px;
  height: 260px;
  object-fit: contain;
}

.validation-mini-card__platform-name {
  font-size: 0.66rem;
  color: #374151;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.validation-mini-card__platform-host {
  font-size: 0.6rem;
  color: #6b7280;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.validation-mini-card__tooltip-content {
  min-width: 220px;
  max-width: 320px;
  padding: 8px;
}

:deep(.validation-mini-card-tooltip) {
  border: 1px solid #d1d5db;
  border-radius: 8px;
  background: #ffffff;
  color: #1f2937;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
}

:deep(.validation-mini-card-image-tooltip) {
  padding: 8px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  background: #ffffff;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.2);
}

.validation-mini-card__tooltip-row {
  display: grid;
  grid-template-columns: minmax(80px, auto) minmax(0, 1fr);
  gap: 6px;
  font-size: 0.68rem;
  line-height: 1.3;
}

.validation-mini-card__tooltip-row + .validation-mini-card__tooltip-row {
  margin-top: 4px;
}

.validation-mini-card__tooltip-key {
  font-weight: 700;
  color: #334155;
}

.validation-mini-card__tooltip-value {
  color: #475569;
  word-break: break-word;
}

@media (max-width: 900px) {
  .validation-mini-card {
    grid-template-columns: 1fr;
    gap: 8px;
  }

  .validation-mini-card--max-mode {
    min-width: 0;
  }

  .validation-mini-card__max-content-row {
    grid-template-columns: 1fr;
  }
}
</style>
